import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  FileInput,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { previewSpreadsheet, type SpreadsheetPreview } from "@/api/catalogs";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";

/** The preview render — split out so it's unit-testable without driving the
 * (browser-native) file picker. */
export function SpreadsheetPreviewResult({ data }: { data: SpreadsheetPreview }): JSX.Element {
  return (
    <Stack gap="sm">
      <Alert color={data.is_clean ? "green" : "yellow"} title={t("spreadsheet.result_title")}>
        {t("spreadsheet.imported_count")} {data.imported.length}
        {data.skipped.length > 0 && (
          <>
            {" · "}
            {t("spreadsheet.skipped_count")} {data.skipped.length}
          </>
        )}
      </Alert>

      {data.imported.length > 0 && (
        <Paper withBorder p="sm" radius="sm">
          <Text size="sm" fw={600} mb="xs">
            {t("spreadsheet.imported")}
          </Text>
          <Group gap={4}>
            {data.imported.map((s) => (
              <Badge key={s} variant="light">
                {s}
              </Badge>
            ))}
          </Group>
        </Paper>
      )}

      {data.errors.length > 0 && (
        <Paper withBorder p="sm" radius="sm">
          <Text size="sm" fw={600} mb="xs" c="red">
            {t("spreadsheet.parse_errors")}
          </Text>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("spreadsheet.col_sheet")}</Table.Th>
                <Table.Th>{t("spreadsheet.col_row")}</Table.Th>
                <Table.Th>{t("spreadsheet.col_message")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.errors.map((e) => (
                <Table.Tr key={`${e.sheet}:${e.row_index}:${e.message}`}>
                  <Table.Td>{e.sheet}</Table.Td>
                  <Table.Td>{e.row_index}</Table.Td>
                  <Table.Td>{e.message}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {data.validation.errors.length > 0 && (
        <Alert color="red" title={t("spreadsheet.validation_errors")}>
          <Stack gap={2}>
            {data.validation.errors.map((v) => (
              <Text key={v.message} size="sm">
                ✗ {v.message}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Phase 4 (ADR-0040 authoring) — bulk spreadsheet preview.
 *
 * Uploads a clinical-content spreadsheet (the 3-sheet xlsx) and shows a dry-run
 * preview of what it parses into — imported biomarkers, per-row parse errors, and
 * catalog-level validation. No MR, no write (POST .../spreadsheet). Backend
 * enforces `catalog:author`.
 */
export function SpreadsheetUploadPage(): JSX.Element {
  const { org = "", branch } = useParams<{ org: string; branch?: string }>();
  const [file, setFile] = useState<File | null>(null);

  const backPath = branch
    ? `/catalogs/${encodeURIComponent(org)}/branch/${encodeURIComponent(branch)}`
    : `/catalogs/${encodeURIComponent(org)}`;

  const preview = useMutation({
    mutationFn: (f: File) => previewSpreadsheet(org, f),
  });

  const data = preview.data;

  return (
    <AppLayout
      breadcrumbs={[
        { label: t("catalogs.title"), to: "/catalogs" },
        { label: branch ? `${org} / ${branch}` : org, to: backPath },
        { label: t("spreadsheet.title") },
      ]}
    >
      <Stack gap="md" maw={760}>
        <Title order={1}>{t("spreadsheet.title")}</Title>
        <Text c="dimmed" size="sm">
          {t("spreadsheet.subtitle")}
        </Text>

        <Group align="flex-end">
          <FileInput
            label={t("spreadsheet.file_label")}
            placeholder={t("spreadsheet.file_placeholder")}
            accept=".xlsx,.xlsm,.csv"
            value={file}
            onChange={setFile}
            clearable
            w={360}
          />
          <Button
            onClick={() => file !== null && preview.mutate(file)}
            disabled={file === null}
            loading={preview.isPending}
          >
            {t("spreadsheet.preview")}
          </Button>
        </Group>

        {preview.isError && (
          <Alert color="red" title={t("spreadsheet.error_title")}>
            {preview.error instanceof Error ? preview.error.message : t("catalogs.error_generic")}
          </Alert>
        )}

        {data !== undefined && <SpreadsheetPreviewResult data={data} />}
      </Stack>
    </AppLayout>
  );
}
